import { useState, useMemo } from "react";
import { Avatar } from "../Avatar";
import InputField from "../input-field/InputField";
import DefaultButton from "../default-button/DefaultButton";
import Delete from "../../assets/icons/Delete";
import Plus from "../../assets/icons/Plus";
import UserPlus from "../../assets/icons/UserPlus";
import UploadFile from "../../assets/icons/UploadFile";
import { Download } from "../../assets/icons/Download";
import Search from "../../assets/icons/Search";
import { MainUser } from "../../types";
import PageSwapper from "../PageSwapperButtons/PageSwapper";
import { Switch } from "../Switch";

export interface AdminStudent {
    id: number;
    student: MainUser;
    studentId: string;
    className: string;
    totalScore: number;
}

interface AttendanceAdminStudentsTableProps {
    students: AdminStudent[];
    onAddStudent?: () => void;
    onDeleteStudent?: (studentId: number) => void;
    onUploadList?: () => void;
    onDownloadList?: () => void;
    onClassFilterChange?: (className: string) => void;
    onSearchChange?: (value: string) => void;
    onSearchCourse?: () => void;
}

// Mock данные студентов на курсе
const mockStudents: AdminStudent[] = [
    {
        id: 1,
        student: {
            id: 1,
            external_user_id: 1,
            profile_image: null,
            json_saved_data: {
                id: 1,
                role: 'student',
                email: 'aruzhan@example.com',
                mobile: '',
                lastname: 'Барушева',
                firstname: 'Аружан',
                department: '',
                externalId: '123456789',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '123456789',
        className: 'Радиофизика 1 курс',
        totalScore: 26
    },
    {
        id: 2,
        student: {
            id: 2,
            external_user_id: 2,
            profile_image: null,
            json_saved_data: {
                id: 2,
                role: 'student',
                email: 'amanay@example.com',
                mobile: '',
                lastname: 'Балат',
                firstname: 'Аманай',
                department: '',
                externalId: '345678912',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '345678912',
        className: 'Радиофизика 1 курс',
        totalScore: 32
    },
    {
        id: 3,
        student: {
            id: 3,
            external_user_id: 3,
            profile_image: null,
            json_saved_data: {
                id: 3,
                role: 'student',
                email: 'danila@example.com',
                mobile: '',
                lastname: 'Дементьев',
                firstname: 'Данила',
                department: '',
                externalId: '567891234',
                yearOfStudy: null,
                organization: '',
                organizationId: 0,
                subOrganization: '',
                subOrganizationId: 0
            }
        },
        studentId: '567891234',
        className: 'Радиофизика 1 курс',
        totalScore: 27
    },
];

export default function AttendanceAdminStudentsTable({
    students = mockStudents,
    onAddStudent,
    onDeleteStudent,
    onUploadList,
    onDownloadList,
    onClassFilterChange,
    onSearchChange,
    onSearchCourse,
}: AttendanceAdminStudentsTableProps) {
    const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set());
    const [searchValue, setSearchValue] = useState("");
    const [selectedClass, setSelectedClass] = useState<string>("Все классы (45)");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [activePage, setActivePage] = useState<string>("search");

    const handleButtonClick = (value: string) => {
        setActivePage(value);
        if (value === "search") {
            onSearchCourse?.();
        } else {
            onAddStudent?.();
        }
    };

    // Получаем уникальные классы
    const classes = useMemo(() => {
        const uniqueClasses = new Set(students.map(s => s.className));
        return Array.from(uniqueClasses);
    }, [students]);

    // Фильтрация и сортировка студентов
    const filteredAndSortedStudents = useMemo(() => {
        let filtered = students.filter(student => {
            const matchesSearch = !searchValue || 
                `${student.student.json_saved_data.firstname} ${student.student.json_saved_data.lastname}`
                    .toLowerCase().includes(searchValue.toLowerCase()) ||
                student.className.toLowerCase().includes(searchValue.toLowerCase());
            
            const matchesClass = selectedClass === "Все классы (45)" || student.className === selectedClass;
            
            return matchesSearch && matchesClass;
        });

        if (sortConfig) {
            filtered.sort((a, b) => {
                let aValue: any;
                let bValue: any;

                switch (sortConfig.key) {
                    case 'student':
                        aValue = `${a.student.json_saved_data.firstname} ${a.student.json_saved_data.lastname}`;
                        bValue = `${b.student.json_saved_data.firstname} ${b.student.json_saved_data.lastname}`;
                        break;
                    case 'class':
                        aValue = a.className;
                        bValue = b.className;
                        break;
                    case 'score':
                        aValue = a.totalScore;
                        bValue = b.totalScore;
                        break;
                    default:
                        return 0;
                }

                if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [students, searchValue, selectedClass, sortConfig]);

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedStudents(new Set(filteredAndSortedStudents.map(s => s.id)));
        } else {
            setSelectedStudents(new Set());
        }
    };

    const handleSelectStudent = (studentId: number, checked: boolean) => {
        const newSelected = new Set(selectedStudents);
        if (checked) {
            newSelected.add(studentId);
        } else {
            newSelected.delete(studentId);
        }
        setSelectedStudents(newSelected);
    };

    const handleSort = (key: string) => {
        setSortConfig(prev => {
            if (prev?.key === key) {
                return { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
            }
            return { key, direction: 'asc' };
        });
    };

    const handleSearchChange = (value: string) => {
        setSearchValue(value);
        onSearchChange?.(value);
    };

    const handleClassFilterChange = (value: string) => {
        setSelectedClass(value);
        onClassFilterChange?.(value);
    };

    const SortIcon = ({ columnKey }: { columnKey: string }) => {
        if (sortConfig?.key !== columnKey) return null;
        return (
            <span className="ml-1">
                {sortConfig.direction === 'asc' ? '↑' : '↓'}
            </span>
        );
    };

    const handleChangeSwitch = (value: string) => () => {
        setActivePage(value);
        if (value === "search") {
            onSearchCourse?.();
        } else if (value === "add") {
            onAddStudent?.();
        }
    };
    return (
        <div className="bg-white rounded-lg p-4 flex flex-col h-full min-h-0 overflow-hidden">
            {/* Заголовок и фильтры */}
            <div className="flex flex-col gap-3 mb-4 flex-shrink-0">
                {/* Заголовок и фильтр по классам */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 flex-wrap">
                    <div className="text-xl md:text-2xl font-semibold flex items-center">
                        Студенты на курсе
                    </div>
                    <select
                        value={selectedClass}
                        onChange={(e) => handleClassFilterChange(e.target.value)}
                        className="bg-[#F0F3FA] rounded-lg px-3 md:px-4 py-2 text-valley-black border-none outline-none flex-shrink-0 text-sm md:text-base"
                    >
                        <option value="Все классы (45)">Все классы (45)</option>
                        {classes.map(cls => (
                            <option key={cls} value={cls}>{cls}</option>
                        ))}
                    </select>
                    <div className="flex gap-2 flex-wrap !text-[13px]">
                        <DefaultButton
                            buttonText="Загрузить список"
                            Icon={UploadFile}
                            additionalStyle="text-valley-gray border border-valley-gray rounded-lg py-2 px-2 md:px-3 flex-shrink-0 whitespace-nowrap "
                            onClick={onUploadList}
                        />
                        <DefaultButton
                            buttonText="Выгрузить список"
                            Icon={Download}
                            additionalStyle="text-valley-gray border border-valley-gray rounded-lg py-2 px-2 md:px-3 flex-shrink-0 whitespace-nowrap "
                            onClick={onDownloadList}
                        />
                    </div>
                </div>

                {/* Поиск и добавление студента */}
                <div className="flex flex-row items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <InputField
                            type="text"
                            placeholder="Поиск"
                            value={searchValue}
                            handleChange={(e) => handleSearchChange(e.target.value)}
                            additionalClassName="!bg-[#F0F3FA] py-2 px-4 rounded-lg w-full"
                        />
                    </div>
                    <div className="flex-shrink-0">
                        <Switch 
                            options={[{label: "Поиск", value: "search", icon: Search}, {label: "Добавить", value: "add", icon: UserPlus}]} 
                            value={activePage} 
                            handleChange={handleChangeSwitch} 
                            wrapperClassName="flex flex-col w-[180px]" 
                            switchClassName="!h-[42px] !mt-0"
                            labelClassName="hidden"
                            id="attendance-admin-students-table-switch" 
                            label="" 
                        />
                    </div>
                </div>
            </div>

            {/* Таблица студентов */}
            <div className="flex-1 overflow-auto min-h-0">
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse min-w-[600px]">
                        <thead>
                            <tr className="border-b border-valley-background-blue text-valley-gray text-xs md:text-sm font-medium">
                                <th className="text-left py-2 px-2 md:px-4">
                                    <input
                                        type="checkbox"
                                        checked={selectedStudents.size === filteredAndSortedStudents.length && filteredAndSortedStudents.length > 0}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                        className="cursor-pointer"
                                    />
                                </th>
                                <th className="text-left py-2 px-2 md:px-4">№</th>
                                <th 
                                    className="text-left py-2 px-2 md:px-4 cursor-pointer"
                                    onClick={() => handleSort('student')}
                                >
                                    Студент <SortIcon columnKey="student" />
                                </th>
                                <th 
                                    className="text-left py-2 px-2 md:px-4 cursor-pointer"
                                    onClick={() => handleSort('class')}
                                >
                                    Класс <SortIcon columnKey="class" />
                                </th>
                                <th 
                                    className="text-left py-2 px-2 md:px-4 cursor-pointer"
                                    onClick={() => handleSort('score')}
                                >
                                    Общий балл <SortIcon columnKey="score" />
                                </th>
                                <th className="text-center py-2 px-2 md:px-4"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredAndSortedStudents.map((student, index) => (
                                <tr 
                                    key={student.id} 
                                    className="border-b border-valley-background-blue hover:bg-valley-background-blue/50"
                                >
                                    <td className="py-2 px-2 md:px-4">
                                        <input
                                            type="checkbox"
                                            checked={selectedStudents.has(student.id)}
                                            onChange={(e) => handleSelectStudent(student.id, e.target.checked)}
                                            className="cursor-pointer"
                                        />
                                    </td>
                                    <td className="py-2 px-2 md:px-4 text-sm">{index + 1}</td>
                                    <td className="py-2 px-2 md:px-4">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <Avatar
                                                user={student.student}
                                            />
                                            <span className="text-sm truncate">
                                                {student.student.json_saved_data.firstname} {student.student.json_saved_data.lastname}
                                            </span>
                                        </div>
                                    </td>
                                    <td className="py-2 px-2 md:px-4 text-sm">{student.className}</td>
                                    <td className="py-2 px-2 md:px-4 text-sm">{student.totalScore}</td>
                                    <td className="py-2 px-2 md:px-4 text-center">
                                        <DefaultButton
                                            Icon={Delete}
                                            additionalStyle="p-1 text-valley-red hover:bg-valley-red/10 rounded"
                                            onClick={() => onDeleteStudent?.(student.id)}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

